// src/services/api.js
import axios from 'axios'
import { useAuthStore } from '@/stores/auth'

// Create axios instance
const api = axios.create({
  timeout: 30000,
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8005/api/v1',
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor - handles both API key and JWT token auth
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()

    // Set base URL from authStore
    if (authStore.baseUrl) {
      config.baseURL = authStore.baseUrl
    }

    // Handle different authentication methods
    if (authStore.authMethod === 'apiKey' && authStore.apiKey) {
      config.headers['x-api-key'] = authStore.apiKey
    } else if (authStore.authMethod === 'password' && authStore.token) {
      config.headers['Authorization'] = `Bearer ${authStore.token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handles 401 errors and token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const authStore = useAuthStore()

    if (error.response?.status === 401) {
      // Token expired or invalid - logout and redirect to login
      authStore.logout()

      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

export const apiService = {
  // Basic CRUD operations
  get: (url, config = {}) => api.get(url, config),
  post: (url, data, config = {}) => api.post(url, data, config),
  put: (url, data, config = {}) => api.put(url, data, config),
  delete: (url, config = {}) => api.delete(url, config),
  
  // System Health
  getSystemHealth: () => api.get('/health'),
  
  // Datasets
  getDatasets: () => api.get('/ml/datasets'),
  
  // Flow endpoints
  getFraudIndicators: (params = {}) => api.get('/ml/flow/fraud_indicators', { params }),
  getChurnFeatures: ( params = {}) => api.get(`/ml/churn-features`, { params }),
  getChurnSummary: (params = {}) => api.get('/ml/schools/churn-summary', { params }),
  
  // Analytics endpoints
  getAtRiskStudents: (params = {}) => api.get('/ml/students/at-risk', { params }),
  getPerformancePrediction: (params = {}) => api.get('/ml/data/analytics/performance_prediction', { params }),
  
  // Learning endpoints
  getPersonalizationData: (params = {}) => api.get('/ml/data/learning/personalization', { params }),
  getCurriculumAlignment: (params = {}) => api.get('/ml/data/learning/curriculum_alignment', { params }),
  
  // Finance endpoints
  getCashflowData: (params = {}) => api.get('/ml/data/finance/cashflow', { params }),
  getCashflowPrediction: (params = {}) => api.get('/ml/data/finance/cashflow_prediction', { params }),
  
  // School Management
  getSchools: (params = {}) => api.get('/ml/schools', { params }),
  getSchoolById: (id) => api.get(`/ml/schools/${id}`),
  
  // Activity Logs
  getActivityLogs: (params = {}) => api.get('/ml/schools/activity-logs', { params }),
  bulkLogActivities: (data) => api.post('/ml/schools/activity-logs/bulk', data),
  
  // Chatbot
  getChatbotKnowledge: (params = {}) => api.get('/ml/data/chatbot/knowledge_base', { params }),
  
  // Export functionality
  exportDataset: async (endpoint, params = {}) => {
    const response = await api.get(endpoint, {
      params: { ...params, limit: 10000 },
      responseType: 'blob'
    })
    return response
  },

  // Expense Fraud Endpoints
  getExpenses: (params = {}) => api.get('/expense-fraud/expenses', { params }),
  getExpenseDetail: (id) => api.get(`/expense-fraud/expenses/${id}/detail`),
  createExpense: (data) => api.post('/expense-fraud/expenses', data),
  analyzeExpense: (id) => api.post(`/expense-fraud/expenses/${id}/analyze`),
  bulkAnalyzeExpenses: (data) => api.post('/expense-fraud/expenses/bulk-analyze', data)
}
